<template>
  <div ref="root" class="ak-form-group-wrap" :class="marginBottom">
    <div class="ak-form-group" :class="rootClasses">
      <div
        v-if="hasLabel"
        class="ak-form-group__label"
        :class="customClass"
        :style="{ width: labelWidth + 'px' }"
      >
        <label :for="labelFor" :class="customClass" class="ak-form-label">
          <slot v-if="$slots.label" name="label" />
          <template v-else>{{ label }}</template>
          <span v-if="required" class="required"> *</span>
        </label>
        <div v-if="$slots.helper" class="ak-form-helper">
          <slot name="helper" />
        </div>
      </div>

      <div class="ak-form-group__body" :class="bodyClasses">
        <slot />
      </div>
    </div>

    <p
      v-if="newMessage"
      class="ak-form-error"
      :class="errorClass"
      v-html="formattedMessage"
    />
  </div>
</template>

<script>
import { truthyClassesToString } from '../../utils/helpers'

import { defineComponent, ref, computed, onMounted, watch, inject } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-field type="info" label="Email">
   *   <ak-input />
   * </ak-field>
   */
  name: 'AkField',

  /**
   * @return Object
   * @description Provides the returned object to the components decendants.
   */
  provide() {
    return {
      akField: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String|Object}
     * @description Type (color) of the field and help message, also adds
     * a matching icon, optional. Used by Input, Select and Autocomplete
     */
    type: {
      default: undefined,
      type: [String, Object],
    },

    /**
     * @type {String}
     * @description Field label
     */
    label: {
      default: undefined,
      type: String,
    },

    /**
     * @type {String}
     * @description 	Same as native "for" set on the label
     */
    labelFor: {
      default: undefined,
      type: String,
    },

    /**
     * @type {String|Array|Object}
     * @description Help message text (can contain HTML).
     */
    message: {
      default: undefined,
      type: [String, Array, Object],
    },

    /**
     * @type {Boolean}
     * @description Group label and control on the same line for horizontal
     * forms
     */
    horizontal: Boolean,

    /**
     * @type {Number}
     * @description Size for the label in pixels, so you can line up forms
     */
    labelWidth: {
      type: Number,
      default: null,
    },

    /**
     * @type {String}
     * @description CSS classes to be applied on field label
     */
    customClass: {
      default: undefined,
      type: String,
    },

    /**
     * @type {String}
     * @description CSS classes to be applied on group wrap
     */
    marginBottom: {
      default: undefined,
      type: String,
    },

    /**
     * @type {Boolean}
     * @description Determines if the group will contain addons, this is
     * automatic (adons by default)
     */
    addons: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if label will have a required flag
     */
    required: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { slots }) {
    const newType = ref(props.type)
    const newMessage = ref(props.message)
    const isField = ref(true)
    const parentForm = inject('akForm')
    const root = ref(null)

    /**
     * @returns {String}
     * @description returns the error classes for the field
     */
    const errorClass = computed(() => {
      return truthyClassesToString(props.newType, 'text-')
    })

    /**
     * @returns {Object}
     * @description Returns the classes for the root element of the
     * components
     */
    const rootClasses = computed(() => {
      return {
        'ak-form-group--horizontal': props.horizontal,
      }
    })

    /**
     * @returns {Object}
     * @description Returns the classes for the body element of the
     * components
     */
    const bodyClasses = computed(() => {
      return {
        'ak-form-group__body--has-addons': props.addons,
      }
    })

    /**
     * @returns {Boolean}
     * @description Returns a boolean value to determine if the component
     * has a label or not
     */
    const hasLabel = computed(() => {
      return props.label || slots.label || slots.helper
    })

    /**
     * @return {String|Array}
     * @description Formatted message in case it's an array (each element
     * is separated by <br> tag)
     */
    const formattedMessage = computed(() => {
      if (typeof newMessage.value === 'string') {
        return newMessage.value
      } else {
        let messages = []
        if (Array.isArray(newMessage.value)) {
          newMessage.value.forEach((message) => {
            if (typeof message === 'string') {
              messages.push(message)
            } else {
              for (let key in message) {
                /* istanbul ignore else */
                if (message[key]) {
                  messages.push(key)
                }
              }
            }
          })
        } else {
          for (let key in newMessage.value) {
            /* istanbul ignore else */
            if (newMessage.value[key]) {
              messages.push(key)
            }
          }
        }
        return messages
          .filter((m) => {
            /* istanbul ignore else */
            if (m) return m
          })
          .join(' <br> ')
      }
    })

    const setMessage = (value) => {
      newMessage.value = value
    }

    const setType = (value) => {
      newType.value = value
    }

    // This fixes the double bottom margin with nested ak-field components
    onMounted(() => {
      if (root.value.querySelectorAll('.ak-form-group-wrap').length) {
        root.value.classList.add('mb-none')
      }
    })

    // Set internal type when prop change.
    watch(
      () => props.type,
      (val) => {
        newType.value = val
      }
    )

    // Set internal message when prop change.
    watch(
      () => props.message,
      (val) => {
        newMessage.value = val
      }
    )

    return {
      newType,
      newMessage,
      isField,
      errorClass,
      rootClasses,
      bodyClasses,
      hasLabel,
      parentForm,
      formattedMessage,
      setMessage,
      setType,
      root,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-form-label {
  font-weight: bold;
  font-size: $font-size-small;
}
.ak-form-helper {
  font-size: $font-size-small;
}

.required {
  color: $color-danger;
}

.ak-form-group-wrap {
  margin-bottom: $spacing;
}

.ak-form-group {
  $self: &;

  &--has-addons,
  &--horizontal {
    display: flex;
  }

  &__label {
    margin-bottom: calc($spacing-tiny / 1.5);
    #{$self}--horizontal & {
      // match input height with padding and border top
      border-top: 1px solid transparent;
      margin-top: calc($spacing / 3);
      margin-right: $spacing-small;
      flex-shrink: 0;
    }
  }

  &__body {
    #{$self}--horizontal & {
      flex-grow: 1;
      margin-top: 0;
    }

    &--has-addons {
      display: flex;
      > * + * {
        margin-left: -1px;
      }

      .ak-input {
        flex-grow: 1;
      }

      .ak-btn,
      .ak-input__control,
      .ak-multiselect__tags {
        border-radius: 0 !important;
      }

      > *:first-child {
        &,
        .ak-btn,
        .ak-input__control,
        .ak-multiselect__tags {
          border-top-left-radius: $base-border-radius !important;
          border-bottom-left-radius: $base-border-radius !important;
        }
      }
      > *:last-child {
        &,
        .ak-btn,
        .ak-input__control,
        .ak-multiselect__tags {
          border-top-right-radius: $base-border-radius !important;
          border-bottom-right-radius: $base-border-radius !important;
        }
      }
    }
  }
}
.ak-form-error {
  margin-top: calc($spacing-tiny / 1.5);
  font-size: $font-size-small;
  color: $color-danger;
}
</style>
